import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CurrencyDisplay, Skeleton } from "@/components/shared";
import { clearSupabaseAuthStorage, supabase } from "@/lib/supabase";
import { wipeClientCaches } from "@/lib/queryClient";
import { spentTotalQueryOptions } from "@/lib/api/dashboard";
import { projectQueryOptions } from "@/lib/api/project";
import { projectBudgetTotal } from "@/lib/spend";
import { pendingBotProposalsCountQueryOptions } from "@/lib/api/botProposals";
import { PushNotificationsToggle } from "@/components/pwa";
import { HiddenThemeToggle } from "@/components/shared/HiddenThemeToggle";
import { NAV_GROUPS } from "@/components/layout/navConfig";
import { NavGroupItem } from "@/components/layout/NavGroupItem";
import { AppMark } from "@/components/brand/AppMark";

export function Sidebar() {
  const navigate = useNavigate();
  const projectQ = useQuery(projectQueryOptions());
  const spentQ = useQuery(spentTotalQueryOptions());
  const pendingCountQ = useQuery(pendingBotProposalsCountQueryOptions());
  const spent = spentQ.data ?? 0;
  const budgetTotal = projectBudgetTotal(projectQ.data);
  const progress =
    budgetTotal != null && budgetTotal > 0
      ? Math.min(100, Math.round((spent / budgetTotal) * 100))
      : 0;
  const loadingBudget = projectQ.isLoading || spentQ.isLoading;
  const pendingCount = pendingCountQ.data ?? 0;

  return (
    <aside
      data-testid="app-sidebar"
      className="fixed left-0 top-0 flex h-dvh w-[240px] flex-col border-r border-slate-200 bg-white px-3 py-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 print:hidden"
    >
      <div className="px-2">
        <div className="flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-white">
          <AppMark testId="brand-mark" />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold leading-5">Takács Építési PM</div>
            <div className="truncate text-xs text-slate-200">Projekt menedzsment</div>
          </div>
        </div>
      </div>

      <nav className="mt-4 min-h-0 flex-1 space-y-1 overflow-y-auto px-2">
        {NAV_GROUPS.map((group) => (
          <NavGroupItem
            key={group.to}
            group={group}
            pendingCount={pendingCount}
            density="sidebar"
          />
        ))}
      </nav>

      <div className="mt-3 space-y-2">
        <PushNotificationsToggle />
        <HiddenThemeToggle className="mt-0 opacity-60" />
        <Button
          type="button"
          variant="outline"
          className="w-full"
          data-testid="logout-button"
          onClick={() => {
            void (async () => {
              const { data } = await supabase.auth.getUser();
              const uid = data.user?.id ?? null;
              await supabase.auth.signOut({ scope: "global" });
              clearSupabaseAuthStorage();
              await wipeClientCaches(uid);
              navigate("/login", { replace: true });
            })();
          }}
        >
          Kijelentkezés
        </Button>
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-50">
              Költségkeret
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-400">{progress}%</div>
          </div>
          <div className="mt-2">
            {loadingBudget ? <Skeleton className="h-2 w-full" /> : <Progress value={progress} />}
          </div>
          <div className="mt-2 text-xs text-slate-600 dark:text-slate-400">
            {loadingBudget ? (
              <Skeleton className="h-4 w-32" />
            ) : (
              <>
                <span className="font-medium text-slate-900 dark:text-slate-50">
                  <CurrencyDisplay value={spent} />
                </span>{" "}
                / {budgetTotal == null ? "—" : <CurrencyDisplay value={budgetTotal} />}
              </>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
